import { useEffect, useState, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import { ChevronLeft, Plus, FileText, ExternalLink, User, Crown, RefreshCw, History } from "lucide-react";
import Header from "@/components/Header";
import api from "@/api/client";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";

const STATUS_COLORS = {
  draft: "bg-slate-100 text-slate-700 border-slate-200",
  submitted: "bg-blue-50 text-blue-700 border-blue-200",
  intake: "bg-sky-50 text-sky-700 border-sky-200",
  in_review: "bg-amber-50 text-amber-700 border-amber-200",
  review_comments: "bg-orange-50 text-orange-700 border-orange-200",
  approved: "bg-emerald-50 text-emerald-700 border-emerald-200",
  fees: "bg-teal-50 text-teal-700 border-teal-200",
  issued: "bg-green-100 text-green-800 border-green-300",
  rejected: "bg-rose-50 text-rose-700 border-rose-200",
};

const NEXT_STATUS = {
  draft: "submitted", submitted: "intake", intake: "in_review",
  in_review: "approved", review_comments: "in_review",
  approved: "fees", fees: "issued",
};

export default function AdminCustomerDetail() {
  const { id } = useParams();
  const [customer, setCustomer] = useState(null);
  const [permits, setPermits] = useState([]);
  const [funnel, setFunnel] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [{ data: cust }, { data: perm }, { data: fun }] = await Promise.all([
        api.get(`/admin/customers/${id}`),
        api.get(`/admin/customers/${id}/permits`),
        api.get(`/admin/customers/${id}/funnel`),
      ]);
      setCustomer(cust);
      setPermits(perm.permits || []);
      setFunnel(fun);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load customer");
    } finally { setLoading(false); }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const handleAdvance = async (permit) => {
    const next = NEXT_STATUS[permit.status];
    if (!next) { toast.info("Permit is in a terminal state"); return; }
    try {
      await api.post(`/admin/permits/${permit.id}/status`, { status: next, note: `Advanced by admin` });
      toast.success(`${permit.project_name} → ${next}`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Status change failed");
    }
  };

  const handleNewPermit = async () => {
    const project = window.prompt("Project name:");
    if (!project) return;
    const ptype = window.prompt("Permit type:", "Building Permit");
    if (!ptype) return;
    try {
      await api.post(`/admin/customers/${id}/permits`, {
        project_name: project, permit_type: ptype, jurisdiction: "county",
      });
      toast.success(`Draft created on behalf of ${customer?.email}`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Create failed");
    }
  };

  if (loading && !customer) return (
    <div className="min-h-screen bg-slate-50"><Header />
      <main className="max-w-screen-2xl mx-auto px-6 py-12 text-sm text-slate-500">Loading customer…</main>
    </div>
  );

  if (!customer) return (
    <div className="min-h-screen bg-slate-50"><Header />
      <main className="max-w-screen-2xl mx-auto px-6 py-12">
        <Link to="/admin/customers" className="text-sm text-slate-500 hover:text-slate-900">← Customers</Link>
        <div className="mt-6 text-slate-700">Customer not found.</div>
      </main>
    </div>
  );

  return (
    <div className="min-h-screen bg-slate-50">
      <Header />
      <main className="max-w-screen-2xl mx-auto px-6 md:px-12 py-12" data-testid="admin-customer-detail-page">
        <Link to="/admin/customers" className="text-xs text-slate-500 hover:text-slate-900 inline-flex items-center gap-1 mb-6">
          <ChevronLeft className="w-3 h-3" />All customers
        </Link>

        <div className="flex items-start justify-between gap-4 flex-wrap mb-8">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-full bg-slate-100 flex items-center justify-center">
              <User className="w-6 h-6 text-slate-500" />
            </div>
            <div>
              <h1 className="font-outfit text-2xl font-bold text-slate-900" data-testid="customer-email">{customer.email}</h1>
              <div className="flex items-center gap-2 mt-1 flex-wrap">
                {customer.is_pro && (
                  <Badge className="bg-amber-100 text-amber-900 border-amber-300 gap-1 text-[10px] font-mono">
                    <Crown className="w-3 h-3" />Pro · {customer.pro_tier}
                  </Badge>
                )}
                {customer.role === "admin" && <Badge variant="outline" className="text-[10px] font-mono">admin</Badge>}
                {customer.city_partner && <Badge variant="outline" className="text-[10px] font-mono bg-indigo-50 border-indigo-200 text-indigo-700">city partner</Badge>}
                <span className="text-[10px] text-slate-500 font-mono">{customer.permit_count} permits · joined {customer.created_at ? new Date(customer.created_at).toLocaleDateString() : "—"}</span>
              </div>
            </div>
          </div>
          <Button onClick={handleNewPermit} className="bg-slate-900 hover:bg-slate-800 text-white" data-testid="customer-new-permit-btn">
            <Plus className="w-3.5 h-3.5 mr-2" />Draft permit on their behalf
          </Button>
        </div>

        {/* Funnel — per-customer permit distribution + avg days in stage */}
        {funnel && funnel.permit_count > 0 && (
          <FunnelCard funnel={funnel} />
        )}

        <div className="bg-white border border-slate-200 rounded-xl overflow-hidden" data-testid="customer-permits-list">
          <div className="px-4 py-3 border-b border-slate-200 flex items-center justify-between">
            <h2 className="text-sm font-semibold text-slate-900 flex items-center gap-2">
              <FileText className="w-3.5 h-3.5 text-slate-500" />Permits
            </h2>
            <Button variant="ghost" size="sm" onClick={load} className="h-7"><RefreshCw className="w-3 h-3 mr-1" />Reload</Button>
          </div>
          {permits.length === 0 ? (
            <div className="px-4 py-8 text-center text-xs text-slate-500" data-testid="customer-permits-empty">
              No permits yet. Click "Draft permit on their behalf" to start the first one.
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-slate-50">
                <tr className="text-left text-[10px] uppercase tracking-wider text-slate-600">
                  <th className="px-4 py-2">Project</th>
                  <th className="px-4 py-2">Type</th>
                  <th className="px-4 py-2">Status</th>
                  <th className="px-4 py-2">Last update</th>
                  <th className="px-4 py-2 text-right"></th>
                </tr>
              </thead>
              <tbody>
                {permits.map((p) => (
                  <tr key={p.id} className="border-t border-slate-100" data-testid={`customer-permit-${p.id}`}>
                    <td className="px-4 py-3">
                      <div className="font-medium text-slate-900">{p.project_name}</div>
                      {p.history?.find(h => h.by_admin_email) && (
                        <div className="text-[10px] text-indigo-600 font-mono flex items-center gap-1 mt-0.5">
                          <History className="w-3 h-3" />Admin-assisted
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3 text-xs text-slate-600">{p.permit_type}</td>
                    <td className="px-4 py-3">
                      <Badge className={`text-[10px] font-mono ${STATUS_COLORS[p.status] || ""}`}>
                        {p.status.replace(/_/g, " ")}
                      </Badge>
                    </td>
                    <td className="px-4 py-3 text-xs text-slate-500 font-mono">
                      {p.updated_at ? new Date(p.updated_at).toLocaleString() : "—"}
                    </td>
                    <td className="px-4 py-3 text-right">
                      {NEXT_STATUS[p.status] && (
                        <Button size="sm" variant="outline" onClick={() => handleAdvance(p)}
                                className="text-xs" data-testid={`advance-${p.id}`}>
                          → {NEXT_STATUS[p.status].replace(/_/g, " ")}
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </main>
    </div>
  );
}


const FUNNEL_ORDER = ["draft", "submitted", "intake", "in_review", "review_comments", "approved", "fees", "issued", "rejected"];

function FunnelCard({ funnel }) {
  const total = funnel.permit_count || 1;
  const max = Math.max(...Object.values(funnel.by_status || { x: 0 }), 1);
  const stages = FUNNEL_ORDER.filter((s) => (funnel.by_status?.[s] || 0) > 0);
  return (
    <div className="mb-6 bg-white border border-slate-200 rounded-xl p-5" data-testid="customer-funnel">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <div className="text-[10px] font-mono uppercase tracking-[0.18em] text-indigo-600">Pipeline Funnel</div>
          <Badge variant="outline" className="text-[10px] font-mono">{funnel.permit_count} permits · {funnel.project_count || 0} projects</Badge>
        </div>
      </div>
      <div className="space-y-2.5">
        {stages.map((s) => {
          const count = funnel.by_status[s];
          const pct = Math.round((count / total) * 100);
          const wpct = (count / max) * 100;
          const avg = funnel.avg_days_in_status?.[s];
          return (
            <div key={s} className="flex items-center gap-3" data-testid={`funnel-row-${s}`}>
              <div className="w-32 text-xs text-slate-700 capitalize">{s.replace(/_/g, " ")}</div>
              <div className="flex-1 h-7 bg-slate-100 rounded-md overflow-hidden relative">
                <div className={`h-full ${STATUS_COLORS[s] || "bg-slate-300"} flex items-center px-3 text-xs font-mono`}
                  style={{ width: `${Math.max(wpct, 4)}%` }}>
                  <span className="font-semibold">{count}</span>
                  <span className="ml-2 opacity-60">{pct}%</span>
                </div>
              </div>
              {avg !== undefined && (
                <Badge variant="outline" className="text-[10px] font-mono whitespace-nowrap min-w-[60px] justify-center">
                  avg {avg}d
                </Badge>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
